// The conversation in the platform agent's panel. Wording from the Amigo design's copy sheet; `steps` labels the
// step list (keys in platformAgentConversation.js). The working line's labels are docs/platform-agent/contracts.md
// §5's table, in services/platform-agent/stepLabels.js.
export default {
  conversation: 'Gesprek',
  working: 'Bezig met denken…',
  stopped: 'Gestopt. Er is niets gewijzigd.',
  step_stopped: 'Gestopt',
  steps_completed_one: '{{count}} stap voltooid',
  steps_completed_other: '{{count}} stappen voltooid',
  chart: {
    table: 'De cijfers in deze grafiek',
    label: 'Label',
    value: 'Waarde',
  },
  // A form of several values Amigo needs at once (ask_form, contracts §5).
  form: {
    submit: 'Doorgaan',
    skip: 'Voorlopig overslaan',
    required: 'Vul dit in om door te gaan',
    generated: 'Wordt voor je aangemaakt zodra je goedkeurt',
    step_of: 'Stap {{index}} van {{total}}',
    on: 'Aan',
    off: 'Uit',
    skipped: 'Voorlopig overgeslagen',
  },
  next_steps: 'Volgende stappen',
  action_card: {
    select_restaurant: 'Dit restaurant selecteren',
    open_page: 'Wordt geopend in het dashboard',
  },
  not_sent: 'Niet verzonden',
  failed: {
    text: 'Er ging iets mis aan mijn kant, dus er is niets gewijzigd. Probeer het zo meteen opnieuw.',
    retry: 'Opnieuw proberen',
  },
  question: {
    other: 'Iets anders…',
    send: 'Verzenden',
    label: 'Vraag',
    type_answer: 'Iets anders? Typ het hieronder.',
    label_many: 'Vragen',
    step_of: '{{index}} van {{total}}',
    previous: 'Vorige vraag',
    next: 'Volgende vraag',
    dismiss: 'Nu niet',
  },
  unavailable: {
    title: {
      amigo: 'Amigo is momenteel niet beschikbaar.',
      assistant: 'De assistent is momenteel niet beschikbaar.',
    },
    text: 'Er is niets gewijzigd en de rest van het dashboard werkt gewoon. Probeer het over een paar minuten opnieuw.',
  },
  // A chat that goes on over several days (D4): a line before each day's first message, and Today to carry on.
  today: 'Vandaag',
  when: {
    today: 'Vandaag, {{time}}',
    yesterday: 'Gisteren, {{time}}',
    date: '{{date}}, {{time}}',
  },
  new_chat_line: 'Nieuwe chat voor {{restaurant}}. De chats van {{previous}} vind je onder Chats als je teruggaat.',
  // A restaurant selected during a chat with no restaurant selected (D18): the chat carries on there, or a new one starts.
  carried_on_line: 'Nu op {{restaurant}}. Deze chat gaat verder en wordt opgeslagen bij de chats.',
  from_none_line:
    'Nieuwe chat voor {{restaurant}}. Je chat over {{earlier}} staat onder Chats, zonder geselecteerd restaurant.',
  from_none_line_unnamed:
    'Nieuwe chat voor {{restaurant}}. Je eerdere chat staat onder Chats, zonder geselecteerd restaurant.',
  // Someone else's chat, opened by our team (D4): whose it is, by who started it (contracts §6 `started_by.kind`).
  read_only: {
    whose: {
      foodamigos: 'Chat van {{name}} (FoodAmigos)',
      owner: 'Chat van {{name}} (eigenaar)',
      staff: 'Chat van {{name}} (medewerker)',
      partner: 'Chat van {{name}} (partner)',
      other: 'Chat van {{name}}',
      unnamed: 'Chat van iemand anders',
    },
    text: 'Chat van {{name}}. Je kunt hem lezen, maar niet reageren of de kaarten goedkeuren.',
    text_unnamed: 'Chat van iemand anders. Je kunt hem lezen, maar niet reageren of kaarten goedkeuren.',
    new_chat: 'Nieuwe chat',
  },
  // A chat whose eve session can't go on (QA42): read only, and New chat takes the message that didn't go with it.
  ended: {
    text: 'Dit gesprek is beëindigd. Start een nieuw gesprek om verder te gaan.',
    new_chat: 'Nieuwe chat',
    moved_line: 'Nieuwe chat, omdat de vorige was beëindigd',
  },
  composer: {
    label: 'Bericht',
    placeholder: 'Stel een vraag of beschrijf een wijziging',
    placeholder_answer: 'Typ uw antwoord',
    hint: 'Enter om te verzenden · Shift+Enter voor nieuwe regel',
  },
  table: {
    show_all: 'Alle {{count}} rijen tonen',
    show_fewer: 'Minder tonen',
  },
  steps: {
    areas: 'Controleren waarmee ik hier kan helpen',
    area_describe: 'Bekijken: {{area}}',
    restaurants: 'Restaurant zoeken',
    read: 'Je gegevens lezen',
    propose: 'De wijziging voorbereiden',
    change_set_get: 'De wijziging controleren',
    change_set_undo: 'Ongedaanmaken voorbereiden',
    change_set_withdraw: 'Kaart sluiten',
    gap_record: 'Dit noteren voor ons team',
    research: 'Opzoeken hoe dit werkt',
    screen_find: 'De juiste pagina zoeken',
    file_read: 'Je bestand lezen',
    attach_chat_file: 'Je bestand wordt voorbereid',
    shop_check: 'Je shop controleren',
    area_describe_other: 'Dit gedeelte bekijken',
    other: 'Iets controleren',
  },
  // What a step that failed says it couldn't do (QL46), by the area it worked in; `other` is the label without an
  // area. contracts §5's Failed steps, kept the same as STEP_FAILED_LABELS in stepLabels.js (a test holds en and de).
  failed_steps: {
    restaurants: {
      other: 'Restaurant niet gevonden',
    },
    read: {
      menu: 'Kon het menu niet lezen',
      hours: 'Kon de openingstijden niet lezen',
      order_settings: 'Kon de bestelinstellingen niet lezen',
      my_info: 'Kon je gegevens niet lezen',
      marketing: 'Kon je marketing niet lezen',
      gift_cards: 'Kon de cadeaubonnen niet lezen',
      reports: 'Kon je cijfers niet lezen',
      other: 'Kon je gegevens niet lezen',
    },
    propose: {
      menu: 'Kon de menuwijziging niet voorbereiden',
      hours: 'Kon de wijziging van de openingstijden niet voorbereiden',
      order_settings: 'Kon de wijziging van de bestelinstellingen niet voorbereiden',
      my_info: 'Kon de wijziging van je gegevens niet voorbereiden',
      marketing: 'Kon de marketingwijziging niet voorbereiden',
      gift_cards: 'Kon de wijziging van de cadeaubonnen niet voorbereiden',
      other: 'Kon de wijziging niet voorbereiden',
    },
    change_set_get: { other: 'Kon de wijziging niet controleren' },
    change_set_undo: { other: 'Kon ongedaanmaken niet voorbereiden' },
    change_set_withdraw: { other: 'Kon kaart niet sluiten' },
    research: { other: 'Kon niet opzoeken hoe dit werkt' },
    screen_find: { other: 'Kon de pagina niet vinden' },
    file_read: { other: 'Kon je bestand niet lezen' },
    attach_chat_file: { other: 'Je bestand kon niet worden voorbereid' },
    shop_check: { other: 'Kon je shop niet openen' },
  },
  // The areas of contracts §0 as the steps name them ("Looking at {{area}}").
  areas: {
    menu: 'het menu',
    hours: 'de openingstijden',
    order_settings: 'de bestelinstellingen',
    my_info: 'de gegevens van je restaurant',
    marketing: 'marketing',
    reports: 'de rapporten',
  },
};
